class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.pillMaxWidth = 480;
    this.pillGap = 8;
    this.pillNavBuffer = 16;

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    const closeButton =
      this.querySelector('.search-modal__close-button') || this.querySelector('button[type="button"]');
    if (closeButton) closeButton.addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');

    if (this.classList.contains('header__search--pill')) {
      this.setupPillSearch();
    }
  }

  setupPillSearch() {
    const form = this.querySelector('form.search');
    if (!form) return;

    const goToSearchPage = () => {
      const input = form.querySelector('input[name="q"]');
      const term = input?.value?.trim() ?? '';
      if (!term) return false;

      const action = form.getAttribute('action') || '/search';
      const url = new URL(action, window.location.origin);
      url.searchParams.set('q', term);

      const prefixInput = form.querySelector('input[name="options[prefix]"]');
      if (prefixInput?.value) {
        url.searchParams.set('options[prefix]', prefixInput.value);
      }

      window.location.href = url.toString();
      return true;
    };

    if (form.dataset.pillSearchBound !== 'true') {
      form.dataset.pillSearchBound = 'true';

      form.querySelector('button[type="submit"]')?.addEventListener('click', (event) => {
        const predictive = this.querySelector('predictive-search');
        const hasSelected = predictive?.querySelector('[aria-selected="true"] a, button[aria-selected="true"][role="option"]');

        if (hasSelected) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        goToSearchPage();
      });

      form.addEventListener('submit', (event) => {
        const predictive = this.querySelector('predictive-search');
        const hasSelected = predictive?.querySelector('[aria-selected="true"] a, button[aria-selected="true"][role="option"]');

        if (hasSelected) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        goToSearchPage();
      });
    }
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    if (!this.classList.contains('header__search--pill')) {
      document.body.classList.add('overflow-hidden');
    } else {
      this.classList.add('is-open');
      this.bindPillPositionListeners();
      this.positionPillDropdown();
    }

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    if (!this.classList.contains('header__search--pill')) {
      document.body.classList.remove('overflow-hidden');
    } else {
      this.classList.remove('is-open');
      this.unbindPillPositionListeners();
      this.resetPillDropdownPosition();
    }
  }

  bindPillPositionListeners() {
    this.onPillReposition = this.onPillReposition || (() => {
      if (this.detailsContainer?.hasAttribute('open')) this.positionPillDropdown();
    });
    window.addEventListener('resize', this.onPillReposition);
    window.addEventListener('scroll', this.onPillReposition, { passive: true });
  }

  unbindPillPositionListeners() {
    if (!this.onPillReposition) return;
    window.removeEventListener('resize', this.onPillReposition);
    window.removeEventListener('scroll', this.onPillReposition);
  }

  resetPillDropdownPosition() {
    this.style.removeProperty('--pill-search-max-width');
  }

  positionPillDropdown() {
    if (!window.matchMedia('(min-width: 990px)').matches) {
      this.style.removeProperty('--pill-search-max-width');
      return;
    }

    const iconRect = this.summaryToggle?.getBoundingClientRect();
    if (!iconRect) return;

    const nav = document.querySelector('.streetwear-header__nav');
    const navRight = nav?.getBoundingClientRect().right ?? 0;
    const minLeft = navRight + this.pillNavBuffer;
    const spaceLeftOfIcon = iconRect.left - this.pillGap - minLeft;
    const maxWidth = Math.min(this.pillMaxWidth, Math.max(320, spaceLeftOfIcon));

    this.style.setProperty('--pill-search-max-width', `${maxWidth}px`);
  }
}

customElements.define('details-modal', DetailsModal);
